import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardBody, Button, Spinner, Alert } from 'reactstrap';
import { toast } from 'react-hot-toast';
import CryptoJS from 'crypto-js';
import { getToken } from '../../../utility/AuthService';
import { useSelector } from 'react-redux';
import { selectLanguageUse } from '../../../redux/slices/language.Slice';

const ProgressAssessmentNew = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const authToken = getToken();
    const languageUse = useSelector(selectLanguageUse);

    const startNewProgressAssessment = async () => {
        if (!authToken) {
            setError(languageUse === 'English' 
                ? 'Authentication required. Please log in again.' 
                : 'Authentification requise. Veuillez vous reconnecter.');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Import the service dynamically to avoid circular dependencies
            const { startProgressSubmissionService } = await import('../../../services/submissionService');
            
            const result = await startProgressSubmissionService({ authToken });
            
            if (result?.data?.statusCode === 200) {
                const submissionId = result.data.data._id;
                const encryptedId = CryptoJS.AES.encrypt(submissionId, 'mySecretKey').toString();
                
                toast.success(
                    languageUse === 'English' 
                        ? 'Progress Assessment started successfully!' 
                        : 'Évaluation des progrès démarrée avec succès!',
                    { duration: 3000 }
                );
                
                // Navigate to the progress assessment questions
                navigate(`/progress/${encodeURIComponent(encryptedId)}`);
            } else {
                throw new Error(result?.data?.message || 'Failed to start assessment');
            }
        } catch (error) {
            console.error('Error starting progress assessment:', error);
            setError(
                languageUse === 'English'
                    ? `Failed to start Progress Assessment: ${error.message}`
                    : `Échec du démarrage de l'évaluation des progrès: ${error.message}`
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToHistory = () => {
        navigate('/user/history');
    };

    return (
        <div className="container mt-4">
            <div className="row justify-content-center">
                <div className="col-md-8">
                    <Card>
                        <CardHeader className="bg-primary text-white">
                            <CardTitle tag="h4" className="mb-0">
                                <i className="bi bi-graph-up-arrow me-2"></i>
                                {languageUse === 'English' ? 'Start New Progress Assessment' : 'Commencer une nouvelle évaluation des progrès'}
                            </CardTitle>
                        </CardHeader>
                        <CardBody className="p-4">
                            {error && (
                                <Alert color="danger" className="mb-4">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    {error}
                                </Alert>
                            )}

                            <div className="text-center mb-4">
                                <div className="mb-3">
                                    <i className="bi bi-clipboard-data" style={{ fontSize: '3rem', color: '#007bff' }}></i>
                                </div>
                                <h5 className="text-muted">
                                    {languageUse === 'English' 
                                        ? 'Ready to track your progress?' 
                                        : 'Prêt à suivre vos progrès?'}
                                </h5>
                                <p className="text-muted">
                                    {languageUse === 'English'
                                        ? 'Start a new Progress Assessment to measure your improvement over time and track your sustainability journey.'
                                        : 'Commencez une nouvelle évaluation des progrès pour mesurer votre amélioration au fil du temps et suivre votre parcours de durabilité.'}
                                </p>
                            </div>

                            <div className="assessment-info mb-4 p-3 bg-light rounded">
                                <h6 className="mb-3">
                                    <i className="bi bi-info-circle me-2 text-info"></i>
                                    {languageUse === 'English' ? 'What is a Progress Assessment?' : 'Qu\'est-ce qu\'une évaluation des progrès?'}
                                </h6>
                                <ul className="mb-0">
                                    <li>
                                        {languageUse === 'English' 
                                            ? 'Measures improvement over time compared to your Readiness Assessment'
                                            : 'Mesure l\'amélioration au fil du temps par rapport à votre évaluation de préparation'}
                                    </li>
                                    <li>
                                        {languageUse === 'English'
                                            ? 'Tracks progress on sustainability goals and targets'
                                            : 'Suit les progrès sur les objectifs et cibles de durabilité'}
                                    </li>
                                    <li>
                                        {languageUse === 'English'
                                            ? 'Provides insights for continuous improvement strategies'
                                            : 'Fournit des informations pour les stratégies d\'amélioration continue'}
                                    </li>
                                </ul>
                            </div>

                            <div className="d-grid gap-3">
                                <Button 
                                    color="primary" 
                                    size="lg"
                                    onClick={startNewProgressAssessment}
                                    disabled={isLoading}
                                    className="py-3"
                                >
                                    {isLoading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            {languageUse === 'English' ? 'Starting Assessment...' : 'Démarrage de l\'évaluation...'}
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-play-circle me-2"></i>
                                            {languageUse === 'English' ? 'Start Progress Assessment' : 'Commencer l\'évaluation des progrès'}
                                        </>
                                    )}
                                </Button>
                                
                                <Button 
                                    color="outline-secondary" 
                                    onClick={handleBackToHistory}
                                    disabled={isLoading}
                                >
                                    <i className="bi bi-arrow-left me-2"></i>
                                    {languageUse === 'English' ? 'Back to History' : 'Retour à l\'historique'}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ProgressAssessmentNew;
